/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '../../../../../base/api';
import { SetFieldParams } from '../../../../../base/interfaces/params';
import { ChangeLoginStep, ChangePasswordStep } from '../../enums';

export interface ChangePasswordState {
    step: ChangePasswordStep;
    fields: {
        password: string;
        passwordNew: string;
    };
    errors: IError | null;
    shouldScrollToError: boolean;
    passwordResetToken: string;
}

export interface ChangeLoginState {
    step: ChangeLoginStep;
    fields: {
        password: string;
        email: string;
        emailCode?: string;
    };
    errors: IError | null;
    shouldScrollToError: boolean;
}

export interface SettingsState {
    currentLogin: string;
    changePassword: ChangePasswordState;
    changeLogin: ChangeLoginState;
}

const changePasswordInitialState: ChangePasswordState = {
    step: ChangePasswordStep.OldPassword,
    fields: {
        password: '',
        passwordNew: '',
    },
    errors: null,
    shouldScrollToError: false,
    passwordResetToken: '',
};

const changeLoginInitialState: ChangeLoginState = {
    step: ChangeLoginStep.Password,
    fields: {
        password: '',
        email: '',
    },
    errors: null,
    shouldScrollToError: false,
};

const initialState: SettingsState = {
    currentLogin: '',
    changePassword: changePasswordInitialState,
    changeLogin: changeLoginInitialState,
};

export const settingsSlice = createSlice({
    name: 'recruiter-app__settings',
    initialState,
    reducers: {
        getCurrentLogin: () => {},
        setCurrentLogin: (state, action: PayloadAction<string>) => {
            state.currentLogin = action.payload;
        },
        clearData: (state) => {
            state.currentLogin = '';
            state.changePassword = changePasswordInitialState;
            state.changeLogin = changeLoginInitialState;
        },
        setChangePasswordStep: (state, action: PayloadAction<ChangePasswordStep>) => {
            state.changePassword.step = action.payload;
        },
        setPasswordResetToken: (state, action: PayloadAction<string>) => {
            state.changePassword.passwordResetToken = action.payload;
        },
        setChangePasswordField: (state, action: PayloadAction<SetFieldParams>) => {
            state.changePassword.fields = {
                ...state.changePassword.fields,
                [action.payload.id]: action.payload.value,
            };
            if (state.changePassword.errors === null) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [action.payload.errorGroupName || action.payload.id]: removedError, ...errors } =
                state.changePassword.errors;
            state.changePassword.errors = errors;
        },
        setChangePasswordErrors: (state, action: PayloadAction<IError>) => {
            if (state.changePassword.errors) {
                state.changePassword.errors = {
                    ...state.changePassword.errors,
                    ...action.payload,
                };
            } else {
                state.changePassword.errors = action.payload;
            }
        },
        setChangePasswordShouldScrollToError: (state, action: PayloadAction<boolean>) => {
            state.changePassword.shouldScrollToError = action.payload;
        },
        resendConfirmation: {
            reducer: () => {},
            prepare: (recaptchaToken: string | null) => ({ payload: recaptchaToken }),
        },
        changePasswordSubmit: {
            reducer: () => {},
            prepare: (callback?: () => void) => ({ payload: callback }),
        },

        setChangeLoginStep: (state, action: PayloadAction<ChangeLoginStep>) => {
            state.changeLogin.step = action.payload;
        },
        setChangeLoginField: (state, action: PayloadAction<SetFieldParams>) => {
            state.changeLogin.fields = {
                ...state.changeLogin.fields,
                [action.payload.id]: action.payload.value,
            };
            if (state.changeLogin.errors === null) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [action.payload.errorGroupName || action.payload.id]: removedError, ...errors } =
                state.changeLogin.errors;
            state.changeLogin.errors = errors;
        },
        setChangeLoginErrors: (state, action: PayloadAction<IError>) => {
            if (state.changeLogin.errors) {
                state.changeLogin.errors = {
                    ...state.changeLogin.errors,
                    ...action.payload,
                };
            } else {
                state.changeLogin.errors = action.payload;
            }
        },
        setChangeLoginShouldScrollToError: (state, action: PayloadAction<boolean>) => {
            state.changeLogin.shouldScrollToError = action.payload;
        },
        changeLoginSubmit: {
            reducer: () => {},
            prepare: (callback?: () => void) => ({ payload: callback }),
        },
        changeLoginSubmitChangeConfirmationValue: {
            reducer: () => {},
            prepare: (newValue: string, recaptchaToken: string | null, callback: () => void) => ({
                payload: { newValue, recaptchaToken, callback },
            }),
        },
    },
});

export const settingsActions = settingsSlice.actions;
export const settingsReducer = settingsSlice.reducer;
